<script lang="ts">
	/**
	 * The single coupon entry point (Design §14).
	 *
	 * The server answers **every** failure mode with the identical body — unknown, inactive,
	 * expired, exhausted, wrong product, wrong currency — so this component can only say "that
	 * code cannot be applied". Inventing a more specific message would rebuild client-side the
	 * oracle §12 removed: validity plus a computed discount, for a short human-typed code that is
	 * worth money.
	 *
	 * The endpoint is behind `requireAuth`, so a signed-out visitor gets a sign-in prompt rather
	 * than a field that always fails.
	 */

	import { Button, Input } from 'flowbite-svelte';
	import { createEventDispatcher } from 'svelte';
	import PriceDisplay from './PriceDisplay.svelte';
	import { validateCoupon } from '../AppNetwork';
	import AbstractID from '../AbstractID';
	import type { CouponValidation, StorefrontCurrency } from '../AppTypes';

	export let priceUUID: string = '';
	export let currencyCode: string = '';
	export let currency: StorefrontCurrency | undefined = undefined;
	export let isSignedIn: boolean = false;
	/** Set false to hide the field entirely — a coupon on an unavailable price is meaningless. */
	export let enabled: boolean = true;
	/**
	 * Why a code cannot be applied right now, rendered in place of the form, or empty (§4.8).
	 *
	 * The provider takes **one** discount per checkout, so a code and a discounting product option
	 * cannot both apply and the server refuses the pair. The alternatives are both worse than
	 * saying so: silently dropping one charges a customer as though they had not typed a code they
	 * did type, and letting them type it produces a refusal at the payment page about a control
	 * that is no longer on screen. A sentence rather than a boolean, because only the caller knows
	 * what is blocking it.
	 */
	export let blockedReason: string = '';

	/**
	 * A code the customer arrived with rather than typed — from `/Product/:slug?coupon=…`, so a
	 * campaign can hand out a link that prices itself (§4.1).
	 *
	 * It is applied **once**, through exactly the same `validateCoupon` call a typed code goes
	 * through: a link is not authority to discount anything, it only saves the customer typing. A
	 * code that does not apply says the same uniform sentence a typed one does, for the reason §12
	 * gives — a link is an even better oracle for guessing codes than a form, because it can be
	 * fired in bulk.
	 */
	export let initialCode: string = '';

	let code: string = '';
	let checking: boolean = false;
	/**
	 * Whether the arriving code has had its one attempt.
	 *
	 * Once, and never again — the customer may clear it, and a flag that let it re-apply would put
	 * the discount back under somebody who had just removed it. It is also why this is not simply
	 * `code === initialCode`: that would re-fire the moment the selected price changed.
	 */
	let initialApplied: boolean = false;
	let validation: CouponValidation | null = null;
	let failed: boolean = false;

	const dispatcher = createEventDispatcher();

	/**
	 * Apply the arriving code as soon as it can be, and not before.
	 *
	 * The conditions are all real: there is no point validating against no price, a signed-out
	 * visitor cannot call `validateCoupon` at all (it is `requireAuth`), and a store that is closed
	 * is not pricing anything. Signing in **inside the purchase flow** flips `isSignedIn`, so a
	 * customer who arrives on a discount link signed out still gets their code applied — a moment
	 * later, without having to remember it.
	 */
	$: if (
		!initialApplied &&
		initialCode.trim().length > 0 &&
		enabled &&
		isSignedIn &&
		priceUUID.length > 0
	) {
		initialApplied = true;
		code = initialCode.trim();
		void fnApply();
	}

	async function fnApply(): Promise<void> {
		if (code.trim().length === 0 || checking) return;

		checking = true;
		failed = false;
		validation = null;
		try {
			const result = await validateCoupon({ code: code.trim(), priceUUID, currency: currencyCode });
			if (result.valid) {
				validation = result;
				dispatcher('applied', { code: code.trim(), validation: result });
			} else {
				failed = true;
				dispatcher('cleared');
			}
		} catch (error) {
			// A rate-limit refusal lands here too, and reads the same way to the customer: the
			// code did not apply. Saying more would leak how many attempts are left.
			failed = true;
			dispatcher('cleared');
		} finally {
			checking = false;
		}
	}

	/**
	 * To the identity provider, like every sign-in that is not a purchase (§14).
	 *
	 * The inline code flow exists so that *buying* needs no account; applying a discount to a
	 * product page is not that moment, and putting an email box here would be a second login form
	 * on the catalogue. The return path is this page, so the customer comes back to the product
	 * they were pricing rather than to the storefront root.
	 */
	function fnSignIn(): void {
		AbstractID.beginSignIn(window.location.pathname);
	}

	function fnClear(): void {
		code = '';
		validation = null;
		failed = false;
		dispatcher('cleared');
	}
</script>

{#if enabled}
	<div class="widget bg-depth">
		<div class="card-label mb-2"><i class="fa-solid fa-ticket text-[11px] mr-1" />Discount code</div>

		{#if blockedReason.length > 0}
			<p class="text-sm text-default">{blockedReason}</p>
		{:else if !isSignedIn}
			<p class="text-sm text-default mb-2">Sign in to apply a discount code.</p>
			<Button size="sm" color="alternative" on:click={fnSignIn}>Sign in</Button>
		{:else if validation !== null}
			<div class="flex flex-wrap items-center gap-3">
				<i class="fa-solid fa-circle-check text-green-600 dark:text-green-400" />
				<span class="text-sm font-semibold">{code.trim().toUpperCase()} applied</span>
				<PriceDisplay
					amount={validation.finalAmount}
					{currency}
					{currencyCode}
					showInterval={false}
					showTaxNote={false}
					showTrial={false}
					size="sm"
				/>
				<Button size="xs" color="alternative" on:click={fnClear}>Remove</Button>
			</div>
			{#if validation.duration === 'repeating' && validation.durationInMonths !== null}
				<p class="text-xs text-default mt-1">
					Applies for the first {validation.durationInMonths} months.
				</p>
			{:else if validation.duration === 'forever'}
				<p class="text-xs text-default mt-1">Applies for the life of the subscription.</p>
			{:else if validation.duration === 'once'}
				<p class="text-xs text-default mt-1">Applies to the first payment.</p>
			{/if}
		{:else}
			<form class="flex flex-wrap items-start gap-2" on:submit|preventDefault={fnApply}>
				<Input
					size="sm"
					class="max-w-56"
					placeholder="SAVE20"
					bind:value={code}
					disabled={checking}
					aria-label="Discount code"
				/>
				<Button size="sm" type="submit" color="alternative" disabled={checking || code.trim().length === 0}>
					{#if checking}<i class="fa-solid fa-spinner fa-spin mr-2" />{/if}Apply
				</Button>
			</form>
			{#if failed}
				<p class="text-xs text-red-600 dark:text-red-500 mt-2">
					That code cannot be applied to this item.
				</p>
			{/if}
		{/if}
	</div>
{/if}
